import { useEffect } from "react";
import { Link } from "react-router-dom";
import { ArrowLeft, ShieldCheck } from "lucide-react";
import { SITE } from "@/lib/siteConfig";
import { Footer } from "@/components/landing/Footer";

/**
 * Reusable layout for legal pages (Impressum, Datenschutz, AGB).
 * - Sets document.title
 * - Sets noindex meta
 * - Provides top bar with brand + back link
 * - Provides consistent article shell
 */
export const LegalPage = ({
  eyebrow = "Rechtliches",
  title,
  intro,
  metaDescription,
  testId,
  children,
}) => {
  useEffect(() => {
    document.title = `${title} · ${SITE.brand}`;
    const meta = document.querySelector('meta[name="description"]');
    if (meta && metaDescription) meta.setAttribute("content", metaDescription);
    const robots = document.querySelector('meta[name="robots"]');
    if (robots) robots.setAttribute("content", "noindex, follow");
    window.scrollTo(0, 0);

    return () => {
      document.title =
        "SecurityStaging.de – Alarmanlagen-Staging für leerstehende Immobilien | Deutschlandweit";
      if (robots) robots.setAttribute("content", "index, follow, max-image-preview:large");
    };
  }, [title, metaDescription]);

  return (
    <div className="bg-slate-50 min-h-screen flex flex-col" data-testid={testId}>
      <div className="bg-[#071A2B] text-white border-b border-white/10">
        <div className="ss-container flex h-16 items-center justify-between">
          <Link
            to="/"
            className="flex items-center gap-2"
            data-testid="legal-brand-link"
            aria-label="Zurück zur Startseite"
          >
            <span className="inline-flex h-9 w-9 items-center justify-center rounded-lg bg-[var(--ss-gold)] text-[var(--ss-navy)]">
              <ShieldCheck className="h-5 w-5" strokeWidth={2.5} />
            </span>
            <span className="font-display font-bold text-base tracking-tight">
              SecurityStaging<span className="text-[var(--ss-gold)]">.de</span>
            </span>
          </Link>

          <Link
            to="/"
            className="inline-flex items-center gap-2 text-sm text-slate-200 hover:text-white transition"
            data-testid="legal-back-link"
          >
            <ArrowLeft className="h-4 w-4" />
            <span className="hidden sm:inline">Zurück zur Startseite</span>
            <span className="sm:hidden">Zurück</span>
          </Link>
        </div>
      </div>

      <main className="ss-container py-12 sm:py-16 lg:py-20 flex-1">
        <header className="max-w-3xl">
          <span className="ss-eyebrow">{eyebrow}</span>
          <h1 className="font-display font-semibold tracking-tight text-4xl sm:text-5xl text-slate-900 mt-4">
            {title}
          </h1>
          {intro && (
            <p className="mt-4 text-slate-600 text-base sm:text-lg">{intro}</p>
          )}
        </header>

        <article className="mt-10 sm:mt-12 max-w-3xl bg-white rounded-2xl border border-slate-200 shadow-[0_10px_30px_rgba(2,6,23,0.06)] p-6 sm:p-10 lg:p-12">
          {children}
          <div className="mt-12 pt-6 border-t border-slate-200 text-xs text-slate-500">
            Stand:{" "}
            {new Date().toLocaleDateString("de-DE", { year: "numeric", month: "long" })}
          </div>
        </article>

        <div className="mt-10 max-w-3xl">
          <Link
            to="/"
            className="inline-flex items-center gap-2 text-sm font-semibold text-[#0A2340] hover:text-[#EAB308] transition"
            data-testid="legal-back-bottom"
          >
            <ArrowLeft className="h-4 w-4" />
            Zurück zur Startseite
          </Link>
        </div>
      </main>

      <Footer />
    </div>
  );
};

export const LegalSection = ({ title, children }) => (
  <section className="mt-10 first:mt-0">
    <h2 className="font-display font-semibold text-xl sm:text-2xl text-slate-900 border-b border-slate-200 pb-3">
      {title}
    </h2>
    <div className="mt-5 space-y-3 text-slate-700 leading-relaxed text-sm sm:text-base">
      {children}
    </div>
  </section>
);

export const LegalSubheading = ({ children }) => (
  <h3 className="font-display font-semibold text-base sm:text-lg text-slate-900 mt-6 first:mt-0">
    {children}
  </h3>
);
